import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';

/**
 * Network error types
 */
export enum NetworkErrorType {
  TIMEOUT = 'TIMEOUT',
  OFFLINE = 'OFFLINE',
  SERVER_ERROR = 'SERVER_ERROR',
  CLIENT_ERROR = 'CLIENT_ERROR',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Structured error object
 */
export interface AppError {
  type: NetworkErrorType;
  message: string;
  statusCode?: number;
  originalError?: any;
  isRetryable: boolean;
}

/**
 * API Error response structure
 */
export interface ApiError {
  statusCode: number;
  message: string | string[];
  error?: string;
  timestamp?: string;
  path?: string;
}

/**
 * Check if the device is offline
 */
export const isOffline = (): boolean => {
  return !navigator.onLine;
};

/**
 * Check if error is a timeout error
 */
export const isTimeoutError = (error: any): boolean => {
  return (
    error.code === 'ECONNABORTED' ||
    error.message?.includes('timeout') ||
    error.message?.includes('Network request failed')
  );
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: any): boolean => {
  return (
    isOffline() ||
    isTimeoutError(error) ||
    error.message === 'Network Error' ||
    !error.response
  );
};

/**
 * Extract API error message from response
 */
export const extractErrorMessage = (error: AxiosError<ApiError>): string => {
  if (error.response?.data) {
    const data = error.response.data;

    // Handle array of messages
    if (Array.isArray(data.message)) {
      return data.message.join(', ');
    }

    // Handle single message
    if (typeof data.message === 'string') {
      return data.message;
    }

    // Handle error field
    if (data.error) {
      return data.error;
    }
  }

  return error.message || 'An unexpected error occurred';
};

/**
 * Map HTTP status codes to user-friendly messages
 */
export const getStatusCodeMessage = (statusCode: number): string => {
  const statusMessages: Record<number, string> = {
    400: 'Invalid request. Please check your input.',
    401: 'You are not authorized. Please login again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'This action conflicts with existing data.',
    422: 'The data provided is invalid.',
    429: 'Too many requests. Please try again later.',
    500: 'Server error. Please try again later.',
    502: 'Bad gateway. The server is temporarily unavailable.',
    503: 'Service unavailable. Please try again later.',
    504: 'Gateway timeout. The server is taking too long to respond.',
  };

  return statusMessages[statusCode] || `Request failed with status ${statusCode}`;
};

/**
 * Determine if an error is retryable
 */
export const isRetryableError = (error: AxiosError): boolean => {
  // Network errors are retryable
  if (isNetworkError(error)) {
    return true;
  }

  // Timeout errors are retryable
  if (isTimeoutError(error)) {
    return true;
  }

  // Specific HTTP status codes that are retryable
  const retryableStatusCodes = [408, 429, 500, 502, 503, 504];
  if (error.response && retryableStatusCodes.includes(error.response.status)) {
    return true;
  }

  return false;
};

/**
 * Handle and normalize errors from API calls
 */
export const handleApiError = (error: any): AppError => {
  // Check if it's an Axios error
  if (error.isAxiosError) {
    const axiosError = error as AxiosError<ApiError>;

    // Handle offline state
    if (isOffline()) {
      return {
        type: NetworkErrorType.OFFLINE,
        message: 'No internet connection. Please check your network.',
        isRetryable: true,
        originalError: error,
      };
    }

    // Handle timeout
    if (isTimeoutError(axiosError)) {
      return {
        type: NetworkErrorType.TIMEOUT,
        message: 'Request timed out. Please try again.',
        isRetryable: true,
        originalError: error,
      };
    }

    // Handle network error (no response)
    if (isNetworkError(axiosError)) {
      return {
        type: NetworkErrorType.OFFLINE,
        message: 'Network error. Please check your connection.',
        isRetryable: true,
        originalError: error,
      };
    }

    // Handle HTTP errors
    if (axiosError.response) {
      const statusCode = axiosError.response.status;
      const message = extractErrorMessage(axiosError);

      // Server errors (5xx)
      if (statusCode >= 500) {
        return {
          type: NetworkErrorType.SERVER_ERROR,
          message,
          statusCode,
          isRetryable: isRetryableError(axiosError),
          originalError: error,
        };
      }

      // Client errors (4xx)
      return {
        type: NetworkErrorType.CLIENT_ERROR,
        message,
        statusCode,
        isRetryable: isRetryableError(axiosError),
        originalError: error,
      };
    }
  }

  // Handle unknown errors
  return {
    type: NetworkErrorType.UNKNOWN,
    message: error.message || 'An unexpected error occurred',
    isRetryable: false,
    originalError: error,
  };
};

/**
 * Retry logic with exponential backoff
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const appError = handleApiError(error);

      // Don't retry if error is not retryable
      if (!appError.isRetryable) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);

      console.log(
        `Request failed (attempt ${attempt + 1}/${maxRetries + 1}). Retrying in ${delay}ms...`
      );

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

/**
 * Format error for display to user
 */
export const formatErrorForDisplay = (error: AppError): string => {
  switch (error.type) {
    case NetworkErrorType.OFFLINE:
      return 'You are offline. Please check your internet connection.';
    case NetworkErrorType.TIMEOUT:
      return 'Request timed out. Please try again.';
    case NetworkErrorType.SERVER_ERROR:
      return error.message || 'Server error. Please try again later.';
    case NetworkErrorType.CLIENT_ERROR:
      return error.message || 'Invalid request. Please check your input.';
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Capture a non-API error to Sentry (SDK failures, native plugins, parse errors).
 * These bypass the Axios interceptor so need explicit capture.
 */
export const captureError = (error: unknown, context: string, extra?: Record<string, any>): void => {
  console.error(`[${context}]`, error);
  const err = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(err, { tags: { context }, extra });
};

/**
 * Log error for debugging (can be extended to send to error tracking service)
 */
export const logError = (error: AppError, context?: string): void => {
  console.error('[Error Handler]', {
    context,
    type: error.type,
    message: error.message,
    statusCode: error.statusCode,
    isRetryable: error.isRetryable,
    timestamp: new Date().toISOString(),
    originalError: error.originalError,
  });

  // Send unexpected errors to Sentry — skip noise:
  // - 4xx client errors (expected validation/auth failures)
  // - OFFLINE/TIMEOUT (transient network issues, not actionable bugs)
  const isExpectedClientError =
    error.statusCode && error.statusCode >= 400 && error.statusCode < 500;
  const isOfflineError = error.type === NetworkErrorType.OFFLINE;
  if (!isExpectedClientError && !isOfflineError && error.originalError) {
    Sentry.captureException(error.originalError, {
      tags: { errorType: error.type },
      extra: { context, statusCode: error.statusCode, isRetryable: error.isRetryable },
    });
  }
};

/**
 * Check if error should trigger logout (401 Unauthorized)
 */
export const shouldLogout = (error: AppError): boolean => {
  return error.statusCode === 401;
};

/**
 * Check if error should show generic message
 */
export const shouldShowGenericError = (error: AppError): boolean => {
  return error.type === NetworkErrorType.UNKNOWN || error.statusCode === 500;
};
